/**
 * The palette's icon set — INLINE, and `currentColor` throughout (2026-08-19).
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ⚠️ WHY THIS EXISTS BESIDE `WidgetIcons`, AND WHAT IS ACTUALLY WRONG WITH THAT ONE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `WidgetIcons` renders each type as a Mantine `<Image>` pointing at an `.svg`
 * file, and every one of those files paints itself `#4c6ef5` — a literal, in the
 * markup. That has three consequences the palette cannot live with:
 *
 *  1. **It cannot take a hover or an active colour.** An `<img>` is opaque to CSS
 *     `color`, so a tile can never darken its glyph under the pointer or invert it
 *     when its family is selected. Both are load-bearing in the hover-tray design
 *     — the tray IS the affordance, and an affordance that does not respond reads
 *     as a picture rather than a control.
 *  2. **It ignores the theme.** `#4c6ef5` is a mid-blue that is simultaneously the
 *     wrong hue (the app's accent is teal) and the wrong value in dark mode.
 *  3. **It is a network request per icon**, and the bar shows five to eight at once.
 *
 * So these are hand-drawn as inline `<svg>` on a 24-unit grid with
 * `stroke: currentColor`, which makes all three problems disappear at once — the
 * glyph is simply text-coloured, like every other icon in the app (Tabler is the
 * same idiom).
 *
 * They are ported from `docs/prs/TV1-2291/prototypes/prototype-d-palette-bar.html`
 * verbatim, on the ticket owner's instruction ("use the icons proposed for this and
 * the extended full menu") — the prototype is what was reviewed and chosen, so the
 * shapes must not drift from it in translation.
 *
 * `WidgetIcons` is deliberately LEFT IN PLACE rather than deleted: nothing else
 * consumes it today, but it is the only place the 600×600 artwork is referenced,
 * and removing files is a separate decision from adding a set. If nothing picks it
 * up, it can go in a later sweep.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FAMILY GLYPHS have no equivalent in `WidgetIcons` at all, and their absence
 * is why the old bar borrowed `types[0]`'s icon for each group — so "Números" wore
 * the KPI glyph and was indistinguishable from the KPI entry inside it. A family is
 * not its first member; it gets its own mark.
 */
import type { ReactNode } from 'react';
import type { WidgetType } from '../types';
import type { WidgetGroup } from '../../widgetCatalog';

type GlyphProps = {
  /** Rendered size in px. The viewBox is 24 units, so this is a pure scale. */
  size?: number;
};

/**
 * One shared wrapper so every glyph has identical stroke geometry.
 *
 * `strokeWidth: 1.7` at 24 units is the prototype's value and is the reason the
 * set reads as one family — a mixed stroke weight is the single most visible way
 * for hand-drawn icons to look unrelated.
 */
const Svg = ({ size = 18, children }: GlyphProps & { children: ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    // Decorative: every call site pairs the glyph with a text label, so announcing
    // it again would make a screen reader read each palette entry twice.
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

/* ── The sixteen widget types ─────────────────────────────────────────────── */

const Kpi = ( p: GlyphProps ) => (
  <Svg {...p}><path d="M4 19V5" /><path d="M4 19h16" /><path d="M8 15l4-6 3 4 4-7" /></Svg>
);

const Count = ( p: GlyphProps ) => (
  <Svg {...p}><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 7h6" /><path d="M12 7v10" /></Svg>
);

const AreaChart = ( p: GlyphProps ) => (
  <Svg {...p}><path d="M3 17l5-6 4 3 5-7 4 4v6H3z" /></Svg>
);

const BarChart = ( p: GlyphProps ) => (
  <Svg {...p}><rect x="4" y="11" width="4" height="9" /><rect x="10" y="6" width="4" height="14" /><rect x="16" y="14" width="4" height="6" /></Svg>
);

const LineChart = ( p: GlyphProps ) => (
  <Svg {...p}><polyline points="3,16 8,10 12,13 16,6 21,9" /></Svg>
);

const PieChart = ( p: GlyphProps ) => (
  <Svg {...p}><circle cx="12" cy="12" r="8" /><path d="M12 4v8h8" /></Svg>
);

const DonutChart = ( p: GlyphProps ) => (
  <Svg {...p}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3.4" /></Svg>
);

const ScatterChart = ( p: GlyphProps ) => (
  <Svg {...p}><path d="M4 20V4" /><path d="M4 20h16" /><circle cx="9" cy="15" r="1.5" /><circle cx="13" cy="9" r="1.5" /><circle cx="18" cy="13" r="1.5" /></Svg>
);

const Table = ( p: GlyphProps ) => (
  <Svg {...p}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /><path d="M9 9v11" /></Svg>
);

const List = ( p: GlyphProps ) => (
  <Svg {...p}><path d="M8 6h12" /><path d="M8 12h12" /><path d="M8 18h12" /><circle cx="4" cy="6" r="1" /><circle cx="4" cy="12" r="1" /><circle cx="4" cy="18" r="1" /></Svg>
);

const CriticalStock = ( p: GlyphProps ) => (
  <Svg {...p}><path d="M12 3l8 4v10l-8 4-8-4V7z" /><path d="M12 12l8-4" /><path d="M12 12v9" /><path d="M12 12L4 8" /></Svg>
);

const Alert = ( p: GlyphProps ) => (
  <Svg {...p}><path d="M12 4l9 16H3z" /><path d="M12 10v4" /><circle cx="12" cy="17.2" r="0.6" fill="currentColor" stroke="none" /></Svg>
);

const Progress = ( p: GlyphProps ) => (
  <Svg {...p}><rect x="3" y="9" width="18" height="6" rx="3" /><path d="M3 12h9" /></Svg>
);

const Text = ( p: GlyphProps ) => (
  <Svg {...p}><path d="M4 6h16" /><path d="M4 11h12" /><path d="M4 16h9" /></Svg>
);

const Heading = ( p: GlyphProps ) => (
  <Svg {...p}><path d="M5 5v14" /><path d="M13 5v14" /><path d="M5 12h8" /><path d="M17 12h3" /></Svg>
);

/**
 * `spacer` is RETIRED (it is `deprecated` in the catalog and filtered out of
 * `ALL_WIDGET_TYPES`), but the map below is a TOTAL `Record<WidgetType>` and a
 * persisted layout can still contain one — so it keeps a glyph. A dashed frame,
 * which is what "deliberately empty" looks like.
 */
const Spacer = ( p: GlyphProps ) => (
  <Svg {...p}><rect x="4" y="6" width="16" height="12" rx="2" strokeDasharray="3 3" /></Svg>
);

/* ── The five families ────────────────────────────────────────────────────── */

const FamilyNumbers = ( p: GlyphProps ) => (
  <Svg {...p}><path d="M6 4v16" /><path d="M4 8h4" /><path d="M13 4h5v7h-5z" /><path d="M13 15h5v5h-5z" /></Svg>
);

const FamilyCharts = ( p: GlyphProps ) => (
  <Svg {...p}><path d="M4 20V6" /><path d="M4 20h16" /><path d="M8 16v-4" /><path d="M13 16V8" /><path d="M18 16v-7" /></Svg>
);

const FamilyTables = ( p: GlyphProps ) => (
  <Svg {...p}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /><path d="M9 9v11" /></Svg>
);

const FamilyStatus = ( p: GlyphProps ) => (
  <Svg {...p}><circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 2" /></Svg>
);

const FamilyContent = ( p: GlyphProps ) => (
  <Svg {...p}><path d="M4 6h16" /><path d="M4 11h12" /><path d="M4 16h9" /></Svg>
);

/* ── The maps ─────────────────────────────────────────────────────────────── */

/**
 * ⚠️ A TOTAL `Record<WidgetType, …>`, on purpose. Widening the `WidgetType` union
 * makes this refuse to compile until the new type has a glyph — which is the only
 * mechanism stopping a palette entry from rendering as a blank square. Same
 * contract `WidgetIcons` carries, kept deliberately.
 */
export const WIDGET_GLYPH: Record<WidgetType, ( props: GlyphProps ) => ReactNode> = {
  kpi: Kpi,
  count: Count,
  areaChart: AreaChart,
  barChart: BarChart,
  lineChart: LineChart,
  pieChart: PieChart,
  donutChart: DonutChart,
  scatterChart: ScatterChart,
  table: Table,
  list: List,
  criticalStock: CriticalStock,
  alert: Alert,
  progress: Progress,
  text: Text,
  heading: Heading,
  spacer: Spacer
};

/** Total over `WidgetGroup` for the same reason. */
export const WIDGET_GROUP_GLYPH: Record<WidgetGroup, ( props: GlyphProps ) => ReactNode> = {
  numbers: FamilyNumbers,
  charts: FamilyCharts,
  tables: FamilyTables,
  status: FamilyStatus,
  content: FamilyContent
};

/** Convenience for a call site that has a type and wants an element. */
export const widgetGlyph = ( type: WidgetType, size = 18 ): ReactNode => {
  const Glyph = WIDGET_GLYPH[type];
  return <Glyph size={size} />;
};

export const widgetGroupGlyph = ( group: WidgetGroup, size = 18 ): ReactNode => {
  const Glyph = WIDGET_GROUP_GLYPH[group];
  return <Glyph size={size} />;
};
